import React, { useMemo, useState } from "react";
import { SLAandAlerts } from "../../Layout/Publish/SLAandAlerts/SLAandAlerts";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Paper,
  Chip,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
} from "@mui/material";

import { Link, useNavigate } from "react-router-dom";
import { Snackbar, Alert } from '@mui/material';
import axios from "axios";
import heartLoading from '../../../assests/heartLoading.gif';
import { isProdEnv } from "../../../env-utils.js";
import EditProductModal from "./ProductViewComponents/EditProductModal.jsx";

const MuiDataTable = ({
  data,
  columns,
  onEdit,
  onView,
  onDelete,
  selectedView,
  searchQuery,
  userRole,
}) => {
  // console.log("role is ", userRole)
  const [order, setOrder] = useState("asc");
  const [orderBy, setOrderBy] = useState("");
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [productToDelete, setProductToDelete] = useState(null);
  const [versioningOpen, setVersioningOpen] = useState(false);
  const [versioningProduct, setVersioningProduct] = useState(null);
  const [versioningId, setVersioningId] = useState(null);
  const [toast, setToast] = React.useState({ open: false, message: '', severity: 'success' });
  const navigate = useNavigate();
  const [openAlertsModal, setOpenAlertsModal] = useState(false);
  const [selectedRowData, setSelectedRowData] = useState(null);
  const [alertsData, setAlertsData] = useState(null);
  const [alertsLoading, setAlertsLoading] = useState(false);
  const isProd = useMemo(() => isProdEnv(), []);
  const [modalOpen, setModalOpen]         = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);


  const showToast = (msg) => {
    setToast(prev => ({
      ...prev,
      open: true,
      message: msg,
      severity: 'success'
    }));
    
    setTimeout(() => setToast(prev => ({
      ...prev,
      open: false
    }))
    , 3000);
  };
  const showFailedToast = (msg) => {
    setToast(prev => ({
      ...prev,
      open: true,
      message: msg,
      severity: 'error'
    }));
    
    setTimeout(() => setToast(prev => ({
      ...prev,
      open: false
    }))
    , 3000);
  };

  const openEdit = (product) => {
    setSelectedProduct(product);
    setModalOpen(true);
  };


  const handleAlertsClick = async (row) => {
    setSelectedRowData(row);
    setAlertsData(null);
    setAlertsLoading(true);
    setOpenAlertsModal(true);
    
    // Fetch alerts data using alerts API
    try {
      const alertsResponse = await axios.get(
        `${process.env.REACT_APP_API_URL}/alerts/get/${row.productId}`
      );
      
      const groupedAlerts = {};
      
      alertsResponse.data.forEach(alert => {
        if (!groupedAlerts[alert.alert_id]) {
          groupedAlerts[alert.alert_id] = {
            AlertId: alert.alert_id,
            AlertType: alert.alert_type === 'TEAMS' ? 'Teams' : 'Email',
            ChannelName: '',
            WebhookLink: '',
            Emails: []
          };
          
          let config = alert.alert_config;
          if (typeof config === 'string') {
            try {
              config = JSON.parse(config);
            } catch (e) {
              console.error('Error parsing alert_config:', e);
            }
          }
          
          if (alert.alert_type === 'TEAMS') {
            groupedAlerts[alert.alert_id].ChannelName = config?.channel_name || '';
            groupedAlerts[alert.alert_id].WebhookLink = config?.webhook_link || '';
          } else if (alert.alert_type === 'EMAIL') {
            if (config?.email) {
              const emails = typeof config.email === 'string' 
                ? config.email.split(',').map(e => e.trim()) 
                : Array.isArray(config.email) 
                  ? config.email 
                  : [config.email];
              groupedAlerts[alert.alert_id].Emails = emails;
            }
          }
        }
      });
      
      // Combine all emails into a single alert entry
      const allEmails = new Set();
      const emailAlertIds = [];
      
      Object.values(groupedAlerts).forEach(alert => {
        if (alert.AlertType === 'Email' && alert.Emails.length > 0) {
          alert.Emails.forEach(email => allEmails.add(email));
          emailAlertIds.push(alert.AlertId);
        }
      });
      
      const combinedAlerts = [];
      
      // Add combined email alert if emails exist
      if (allEmails.size > 0) {
        combinedAlerts.push({
          AlertId: emailAlertIds[0],
          AlertType: 'Email',
          Emails: Array.from(allEmails),
          AllAlertIds: emailAlertIds
        });
      }
      
      // Add Teams alerts separately
      Object.values(groupedAlerts).forEach(alert => {
        if (alert.AlertType === 'Teams') {
          combinedAlerts.push(alert);
        }
      });

      const transformedData = {
        ProductInfo: {
          productId: row.productId,
          productName: row.productName,
          productDescription: row.productDescription
        },
        SLAandAlerts: {
          slaResponseTime: 99,
          slaUptime: 99,
          Alerts: combinedAlerts
        }
      };
      
      setAlertsData(transformedData);
    } catch (error) {
      console.error('Error fetching alerts data:', error);
      setAlertsData({
        ProductInfo: {
          productId: row.productId,
          productName: row.productName,
          productDescription: row.productDescription
        },
        SLAandAlerts: {
          slaResponseTime: 99,
          slaUptime: 99,
          Alerts: []
        }
      });
    } finally {
      setAlertsLoading(false);
    }
  };

  // Refresh alerts data function for SLAandAlerts component
  const refreshAlertsData = async () => {
    if (!selectedRowData?.productId) return;
    setAlertsLoading(true);
    try {
      const alertsResponse = await axios.get(
        `${process.env.REACT_APP_API_URL}/alerts/get/${selectedRowData.productId}`
      );
      
      const groupedAlerts = {};
      
      alertsResponse.data.forEach(alert => {
        if (!groupedAlerts[alert.alert_id]) {
          groupedAlerts[alert.alert_id] = {
            AlertId: alert.alert_id,
            AlertType: alert.alert_type === 'TEAMS' ? 'Teams' : 'Email',
            ChannelName: '',
            WebhookLink: '',
            Emails: []
          };
          
          let config = alert.alert_config;
          if (typeof config === 'string') {
            try {
              config = JSON.parse(config);
            } catch (e) {
              console.error('Error parsing alert_config:', e);
            }
          }
          
          if (alert.alert_type === 'TEAMS') {
            groupedAlerts[alert.alert_id].ChannelName = config?.channel_name || '';
            groupedAlerts[alert.alert_id].WebhookLink = config?.webhook_link || '';
          } else if (alert.alert_type === 'EMAIL') {
            if (config?.email) {
              const emails = typeof config.email === 'string' 
                ? config.email.split(',').map(e => e.trim()) 
                : Array.isArray(config.email) 
                  ? config.email 
                  : [config.email];
              groupedAlerts[alert.alert_id].Emails = emails;
            }
          }
        }
      });
      
      const allEmails = new Set();
      const emailAlertIds = [];
      
      Object.values(groupedAlerts).forEach(alert => {
        if (alert.AlertType === 'Email' && alert.Emails.length > 0) {
          alert.Emails.forEach(email => allEmails.add(email));
          emailAlertIds.push(alert.AlertId);
        }
      });
      
      const combinedAlerts = [];
      
      if (allEmails.size > 0) {
        combinedAlerts.push({
          AlertId: emailAlertIds[0],
          AlertType: 'Email',
          Emails: Array.from(allEmails),
          AllAlertIds: emailAlertIds
        });
      }
      
      Object.values(groupedAlerts).forEach(alert => {
        if (alert.AlertType === 'Teams') {
          combinedAlerts.push(alert);
        }
      });

      const transformedData = {
        ProductInfo: {
          productId: selectedRowData.productId,
          productName: selectedRowData.productName,
          productDescription: selectedRowData.productDescription
        },
        SLAandAlerts: {
          slaResponseTime: 99,
          slaUptime: 99,
          Alerts: combinedAlerts
        }
      };
      
      setAlertsData(transformedData);
    } catch (error) {
      console.error('Error refreshing alerts data:', error);
    } finally {
      setAlertsLoading(false);
    }
  };
  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const ConfirmVersiong = async (id) => {
    try{
      const response = await axios.post(`${process.env.REACT_APP_API_URL}/initiate-versioning/${id}`)
      if (response?.status === 200) {
        setToast({ open: true, message: 'Versioning Initiated Successfully.', severity: 'success' });
        // getchProductsDetails();
        setVersioningOpen(false)
        navigate(`/versioning/${id}`)
        
      }
    } catch (err) {
      setToast({ open: true, message: 'Failed to Initiate Versioning.', severity: 'error' });
    }
  }

  const handleDeleteClick = (productId, productName) => {
    setProductToDelete({ id: productId, name: productName });
    setDeleteDialogOpen(true);
  };

  const handleVersioningPopup = (id, name, currentPhase) => {

    if(currentPhase === "In Versioning"){
      // navigate(`/versioning/${id}`)
      window.location.href = `/versioning/${id}`;
    }else{
      setVersioningProduct(name);
      setVersioningId(id);
      setVersioningOpen(true)
    }
   

  }

  const handleDeleteConfirm = () => {
    if (productToDelete && onDelete) {
      onDelete(productToDelete.id);
    }
    setDeleteDialogOpen(false);
    setProductToDelete(null);
  };
  const hanldeVersioning = () => {
    if (versioningId) {
      ConfirmVersiong(versioningId);
    }
    setVersioningOpen(false)
    setVersioningProduct(null);
    setVersioningId(null);
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setProductToDelete(null);
  };

  const handleVersioningCancel = () => {
    setVersioningOpen(false);
    setVersioningProduct(null);
    setVersioningId(null);


  }

  const sortedData = React.useMemo(() => {
    if (!orderBy) return data;

    return [...data].sort((a, b) => {
      let aVal = a[orderBy];
      let bVal = b[orderBy];

      // Handle different data types
      if (typeof aVal === "string") {
        aVal = aVal.toLowerCase();
        bVal = bVal.toLowerCase();
      }

      if (typeof aVal === "number") {
        return order === "asc" ? aVal - bVal : bVal - aVal;
      }

      if (order === "asc") {
        return aVal < bVal ? -1 : aVal > bVal ? 1 : 0;
      } else {
        return aVal > bVal ? -1 : aVal < bVal ? 1 : 0;
      }
    });
  }, [data, order, orderBy]);

  console.log("sorted data", sortedData)

  const getStatusColor = (status) => {
    switch (status.toLowerCase()) {
      case "product onboarded":
        return "#abf2d3ff";
      case "inactive":
        return "error";
      case "in progress":
        return "#e6dbf2ff";
      case "draft":
        return "info";
      default:
        return "#f6ebf2ff";
    }
  };
  const getTextColor = (status) => {
    switch (status.toLowerCase()) {
      case "product onboarded":
        return "#214838ff";
      case "inactive":
        return "error";
      case "in progress":
        return "#8b36e6ff";
      case "draft":
        return "info";
      default:
        return "#cd0b7fff";
    }
  };


  const getQualityScoreColor = (score) => {
    if (score > 85) return "#4CAF50";
    if (score >= 55) return "#FF9800";
    if (score < 55) return "#F44336";
    return "#000";
  };
  console.log("columns name", columns)
  return (
    <>
      <TableContainer
        component={Paper}
        sx={{ boxShadow: "none",   }}
        className="font-poppins"
      >
        <Table sx={{ '& tbody .MuiTableCell-root': { borderBottom: 'none' } }}>
          <TableHead>
            <TableRow sx={{ backgroundColor: "#F6F6F6" }}>
              
              {columns.filter(item =>item.id !== "versioning").map((column)  => (
                <TableCell 
                  key={column.id}
                  sx={{
                    width: selectedView === "products" ? 
                      column.id === 'productName' ? '15%' :
                      column.id === 'productDescription' ? '35%' :
                      column.id === 'currentPhase' ? '15%' :
                      column.id === 'status' ? '15%' :
                      column.id === 'actions' ? '10%' : 'auto'
                      : 'auto'
                  }}
                >
                  {column.sortable ? (
                    <TableSortLabel
                      active={orderBy === column.id}
                      direction={orderBy === column.id ? order : "asc"}
                      onClick={() => handleRequestSort(column.id)}
                      sx={{ 
                        fontWeight: 600, 
                        fontSize: "12px", 
                        color: "#404040 !important",
                        "&.MuiTableSortLabel-root": {
                          color: "#404040 !important"
                        },
                        "&.MuiTableSortLabel-root.Mui-active": {
                          color: "#404040 !important"
                        },
                        "&.MuiTableSortLabel-root:hover": {
                          color: "#404040 !important"
                        },
                        "& .MuiTableSortLabel-icon": {
                          color: "#404040 !important"
                        }
                      }}
                    >
                      {column.label}
                    </TableSortLabel>
                  ) : (
                    <Box sx={{ fontWeight: 600, fontSize:  "12px", color: "#404040" }}>{column.label}</Box>
                  )}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedData.map((row, index) => (
              <TableRow
                key={row.id || index}
                sx={{
                  border: "none",
                  fontSize: "18px"
                }}
              >
                {selectedView === "products" ? (
                  <>
                    
                    {
                       localStorage.getItem("role") === "Admin" ? (
                        <TableCell
                          onClick={() => openEdit(row)}
                          sx={{
                            fontWeight: 500,
                            width: "15%",
                            fontSize: "12px !important",
                            textDecoration: "underline",
                          }}
                        >
                          {row.productName}
                        </TableCell>
                      ) : (
                        <TableCell
                          sx={{ fontWeight: 500, width: "15%", fontSize: "12px !important" }}
                        >
                          {row.productName}
                        </TableCell>
                      )
                    }
                    <TableCell
                      sx={{
                        fontSize: "12px !important",
                        whiteSpace: "normal",
                        overflow: "hidden",
                      }}
                      title={row.productDescription}
                    >
                      {(() => {
                        const words = row.productDescription?.split(/\s+/) || [];
                        return words.slice(0, 15).join(" ") + (words.length > 15 ? " ..." : "");
                      })()}
                    </TableCell>

          
                    <TableCell sx={{fontSize: "12px !important"}}><Box sx={{display: "flex", justifyContent : 'center'}}>{row?.version || 1}</Box></TableCell>
                    <TableCell sx={{fontSize: '12px !important'}}>
                      <Chip 
                        label={row.status}
                        size="small"
                        variant="filled"
                        sx={{
                          backgroundColor: getStatusColor(row.status),
                          color: getTextColor(row.status),
                          fontSize: '12px !important',
                          border: "none",
               padding: "5px 0",   borderRadius: '6px'}}
                      />
                    </TableCell>
                    <TableCell sx={{fontSize: "12px !important"}}>{row.currentPhase || "N/A"}</TableCell>
                
                    <TableCell sx={{ width: '10%', fontSize: "12px !important", paddingRight: '20px !important' }}>
                      <Box sx={{ display: "flex", gap: 1, flexWrap: "nowrap", alignItems: "center" }}>
                        <button
                          data-mtm-action="click"
                          data-mtm-category="Alert"
                          data-mtm-name={row.productName}
                          onClick={() => handleAlertsClick(row)}
                          className="bg-[#ebe7ca] text-[#998708] p-2 text-[12px] border rounded whitespace-nowrap flex-shrink-0"
                        >
                          Alert
                        </button>
                        {!isProd && <>
                          {(() => {
                            // Always disable if current phase is "Data Contract Publish"
                            if (row.currentPhase === "Data Contract Publish") {
                              return (
                                <button disabled className="bg-gray-200 text-gray-400 p-2 text-[12px] border rounded whitespace-nowrap flex-shrink-0">
                                  Edit
                                </button>
                              );
                            }

                            // Always disable if current phase is "In Versioning"
                            if (row.currentPhase === "In Versioning") {
                              return (
                                <button disabled className="bg-gray-200 text-[12px] text-gray-400 p-2 border rounded whitespace-nowrap flex-shrink-0">
                                  Edit
                                </button>
                              );
                            }

                            // Define roles and phases
                            const dataEngineerVendorRoles = ['Data Engineer', 'Vendor'];
                            const enabledPhasesForDataEngineerVendor = [
                              'Data Asset Selection',
                              'Column Selection',
                              'Profiling',
                              'Data Quality Rule Configuration'
                            ];

                            if (userRole === 'Admin') {
                              // Admin can access all phases except ones already disabled above
                            } else if (dataEngineerVendorRoles.includes(userRole)) {
                              // For Data Engineer and Vendor: only enable specific phases
                              if (!enabledPhasesForDataEngineerVendor.includes(row.currentPhase)) {
                                return (
                                  <button disabled className="bg-gray-200 text-gray-400 p-2 text-[12px] border rounded whitespace-nowrap flex-shrink-0">
                                    Edit
                                  </button>
                                );
                              }
                            }

                            // Enable edit for allowed cases
                            return (
                              <Link to={`/product-onboarding/${row.productId}`}>
                                <button
                                  data-mtm-action="click"
                                  data-mtm-category="Edit"
                                  data-mtm-name={row.productName}
                                  className="bg-[#bb1ccc] text-[#fff] p-2 text-[12px] border rounded whitespace-nowrap flex-shrink-0">
                                  Edit
                                </button>
                              </Link>
                            );
                          })()}

                        {row.status === "Product Onboarded" || row.currentPhase === "In Versioning" ? (
                          userRole === "Admin" || "Product Owner" ? (
                            <button
                              data-mtm-action="click"
                              data-mtm-category={`Edit-V${row?.version || 1}`}
                              data-mtm-name={row.productName}
                              onClick={() => handleVersioningPopup(row.productId, row.productName, row.currentPhase)}
                              className="bg-[#f6ebf2ff] text-[#cd0b7fff] p-2 text-[12px] border rounded whitespace-nowrap flex-shrink-0"
                              title={`edit versioning ${row?.version || 1}`}
                            >
                              Edit-V{row?.version || 1}
                            </button>
                          ) : (
                            <span className="p-2 text-[12px] whitespace-nowrap flex-shrink-0">N/A</span>
                          )
                    ) : (
                      <button disabled className="bg-gray-200 text-gray-400 p-2 text-[12px] border rounded whitespace-nowrap flex-shrink-0">
                        Edit-V{row?.version || 1}
                      </button>
                    )}
                        </>}

                        <button
                          data-mtm-action="click"
                          data-mtm-category="View"
                          data-mtm-name={row.productName}
                          onClick={() => onView && onView(row)}
                          className="bg-blue-100 text-blue-600 p-2 text-[12px] border rounded whitespace-nowrap flex-shrink-0"
                        >
                          View
                        </button>

                        {!isProd && userRole === "Admin" && <button
                          data-mtm-action="click"
                          data-mtm-category="Delete"
                          data-mtm-name={row.productName}
                          onClick={() => handleDeleteClick(row.productId, row.productName)}
                          className="bg-red-100 text-red-600 p-2 text-[12px] border rounded whitespace-nowrap flex-shrink-0"
                        >
                          Delete
                        </button>}
                      </Box>
                    </TableCell>
                  </>
                ) : (
                  <>
                    <TableCell sx={{ fontWeight: 500 }}>{row.name}</TableCell>
                    <TableCell>{row.schema}</TableCell>
                    <TableCell>{row.columns}</TableCell>
                    <TableCell
                      sx={{
                        color: getQualityScoreColor(row.qualityScore),
                        fontWeight: 600,
                      }}
                    >
                      {row.qualityScore}
                    </TableCell>
                    <TableCell>{row.lastUpdated}</TableCell>
                    <TableCell>{row.createdDate}</TableCell>
                  </>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* versioning confirmation Dialog */}
      <Dialog
        open={versioningOpen}
        onClose={handleVersioningCancel}
        aria-labelledby="delete-dialog-title"
        aria-describedby="delete-dialog-description"
      >
        <DialogTitle id="delete-dialog-title">
          Confirm Versioning
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="delete-dialog-description">
            Are you sure you want to do versioning for "{versioningProduct}"? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleVersioningCancel} color="primary">
            Cancel
          </Button>
          <Button onClick={hanldeVersioning} color="success" variant="contained">
            Confirm
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleDeleteCancel}
        aria-labelledby="delete-dialog-title"
        aria-describedby="delete-dialog-description"
      >
        <DialogTitle id="delete-dialog-title">
          Confirm Delete
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="delete-dialog-description">
            Are you sure you want to delete the product "{productToDelete?.name}"? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel} color="primary">
            Cancel
          </Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Alerts Modal */}
      <Dialog
        open={openAlertsModal}
        onClose={() => setOpenAlertsModal(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogContent>
          {alertsLoading ? (
            <div className="flex flex-col items-center justify-center py-12">
              <img src={heartLoading} alt="Loading..." className="h-24 w-24 mb-4" />
              <p className="text-gray-600 text-lg">Loading alerts data...</p>
            </div>
          ) : alertsData ? (
            <SLAandAlerts
              publishData={alertsData}
              onDataRefresh={refreshAlertsData}
              productId={selectedRowData?.productId}
              showProductSLA={false}
            />
          ) : (
            <div className="flex flex-col items-center justify-center py-12">
              <p className="text-gray-500">No alerts data available.</p>
            </div>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenAlertsModal(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Toast Notification */}
      <Snackbar 
        open={toast.open} 
        autoHideDuration={3000} 
        onClose={() => setToast({ ...toast, open: false })} 
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert onClose={() => setToast({ ...toast, open: false })} severity={toast.severity} sx={{ width: '100%' }}>
          {toast.message}
        </Alert>
      </Snackbar>

      <EditProductModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        productId={selectedProduct?.productId}
        initialName={selectedProduct?.productName}
        product={selectedProduct}
        onSuccess={(data) => {
          setModalOpen(false);
          showToast(`"${data?.new_product_name || "N/A"}" updated successfully`);
        }}
        onFailed={(data) => {
          setModalOpen(false);
          showFailedToast(`"${data?.new_product_name || "N/A"}" updated Failed`);
        }}
      />
    </>
  );
};

export default MuiDataTable;
